import { useMemo, type SyntheticEvent } from "react";

import { useHasEnablePackPermissions } from "../hooks/useHasEnablePackPermissions";
import { PackStatusLabelWithIcon } from "../../../../components/packs/packStatusLabelWithIcon/PackStatusLabelWithIcon";

import { getRequestLabelText, isPaidPricingType } from "../utils/packUtils";
import { PackRequestButton } from "components/packs/PackRequestButton/PackRequestButton";
import styles from "./PackStatusActionLabel.module.scss";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";

export interface PackStatusActionLabelProps {
  isTrialAllowed: boolean;
  isOwn: boolean;
  isExpired: boolean;
  isArchived: boolean;
  isPurchased: boolean;
  isRequested: boolean;
  isTrial?: boolean;
  trialPeriodName?: string;
  packPricingTypeId: number;
  isFromTemplate?: boolean;
  isPrivate?: boolean;
  hasPurchasePackAddon: boolean;
  onRequestClick: (e: SyntheticEvent) => void;
  onTrialClick: (e: SyntheticEvent) => void;
  /** When true, only shows requested/own state; omits purchased/added/expired/trial labels */
  minimalStatus?: boolean;
}

export const PackStatusActionLabel: React.FC<PackStatusActionLabelProps> = (props: PackStatusActionLabelProps) => {
  const {
    trialPeriodName = "",
    isTrialAllowed,
    isOwn,
    isExpired,
    isArchived,
    isPurchased,
    isRequested,
    isTrial,
    packPricingTypeId,
    isFromTemplate,
    isPrivate,
    hasPurchasePackAddon,
    onTrialClick,
    onRequestClick,
    minimalStatus,
  } = props;
  const hasEnablePackPermissions = useHasEnablePackPermissions();
  const isAdditionalPurchaseEnabled = !!useFeatureFlag(FeatureFlags.AdditionalPurchase);

  const computedState = useMemo(() => {
    const isPurchasedActive = isPurchased && !isExpired && !isTrial;
    const isPaid = isPaidPricingType(packPricingTypeId);

    return {
      isPurchasedActive,
      canShowTrial: isTrialAllowed && !isOwn && !isExpired && !isPurchasedActive && !isRequested && !isTrial,
      canShowRequest: !isOwn && !isRequested && !isPurchasedActive,
      isButtonDisabled:
        isArchived ||
        (!isPaid && isExpired && !isAdditionalPurchaseEnabled) ||
        isFromTemplate ||
        isPrivate ||
        (!hasEnablePackPermissions && isPurchased && !isAdditionalPurchaseEnabled),
    };
  }, [
    isPurchased,
    isExpired,
    isTrial,
    packPricingTypeId,
    isTrialAllowed,
    isOwn,
    isRequested,
    isArchived,
    isFromTemplate,
    isPrivate,
    hasEnablePackPermissions,
    isAdditionalPurchaseEnabled,
  ]);

  const buttonConfig = useMemo(
    () => ({
      requestLabel: getRequestLabelText(packPricingTypeId, hasPurchasePackAddon),
      trialLabel: `Start Free${trialPeriodName ? ` ${trialPeriodName}` : ""} Trial`,
    }),
    [packPricingTypeId, hasPurchasePackAddon, trialPeriodName],
  );

  const shouldShowRequestButton = computedState.canShowTrial || computedState.canShowRequest;
  if (shouldShowRequestButton) {
    return (
      <PackRequestButton
        className={styles.requestButton}
        isMultiple={computedState.canShowTrial}
        onRequestClick={onRequestClick}
        onTrialClick={onTrialClick}
        triggerText={buttonConfig.requestLabel}
        disabled={computedState.isButtonDisabled}
        startTrialLabel={buttonConfig.trialLabel}
        hasPurchasePermission={hasEnablePackPermissions}
        hasPurchasePackAddon={hasPurchasePackAddon}
      />
    );
  }

  if (minimalStatus) {
    return <PackStatusLabelWithIcon isRequested={isRequested} isOwn={isOwn} />;
  }

  return null;
};
